if (!customElements.get('columns-banners-slider')) {
  class ColumnsBannersSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.columns-banners__slider-viewport');
      this.track = this.querySelector('.columns-banners__slider-track');
      this.prevBtn = this.querySelector('.columns-banners__slider-btn--prev');
      this.nextBtn = this.querySelector('.columns-banners__slider-btn--next');

      this.originalSlides = [...this.track.children];
      this.totalSlides = this.originalSlides.length;
      this.currentIndex = 0;
      this.cloneCount = 0;
      this.isAnimating = false;
      this.isDragging = false;
      this.startX = 0;
      this.dragDelta = 0;
      this._slideWidth = 0;
      this._gap = 0;
      this._peekPx = 0;
      this._prevSlidesPerView = null;
      this._initialized = false;

      this.setupClones();
      this.bindEvents();

      // ResizeObserver fires with the actual computed width — reliable for both
      // initial sizing and responsive changes, unlike requestAnimationFrame.
      this.resizeObserver = new ResizeObserver(entries => {
        const vw = entries[0].contentRect.width;
        if (!vw) return;

        const spv = this.slidesPerView;

        if (!this._initialized) {
          this._initialized = true;
          this._prevSlidesPerView = spv;
        } else if (spv !== this._prevSlidesPerView) {
          this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.totalSlides - 1));
          this._prevSlidesPerView = spv;
          this.setupClones();
        }

        this._applyWidths(vw);
        this.setPosition(false);
      });

      this.resizeObserver.observe(this.viewport);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }

    get isFullWidth() {
      return this.dataset.fullWidth === 'true';
    }

    get slidesPerView() {
      if (this.isFullWidth) return 1;
      return window.innerWidth >= 750 ? 3 : 1;
    }

    get peekPx() {
      return this._peekPx;
    }

    setupClones() {
      this.track.querySelectorAll('[data-clone]').forEach(el => el.remove());

      const count = this.slidesPerView;

      const startClones = this.originalSlides.slice(-count).map(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'start');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });

      const endClones = this.originalSlides.slice(0, count).map(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'end');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });

      [...startClones].reverse().forEach(clone => {
        this.track.insertBefore(clone, this.track.firstChild);
      });
      endClones.forEach(clone => this.track.appendChild(clone));

      this.cloneCount = startClones.length;
    }

    _applyWidths(vw) {
      const computedGap = parseFloat(getComputedStyle(this.track).columnGap);
      this._gap = isNaN(computedGap) ? 0 : computedGap;

      const spv = this.slidesPerView;

      if (this.isFullWidth) {
        // Desktop: adjacent slides 75% visible. Layout: 0.75w + gap + w + gap + 0.75w = vw → w = (vw - 2*gap) / 2.5
        // Mobile:  adjacent slides  5% visible. Layout: 0.05w + gap + w + gap + 0.05w = vw → w = (vw - 2*gap) / 1.1
        const peek = window.innerWidth >= 750 ? 0.5 : 0.05;
        this._slideWidth = (vw - 2 * this._gap) / (1 + 2 * peek);
        this._peekPx = this._gap + peek * this._slideWidth;
      } else if (spv === 1) {
        this._peekPx = 32;
        this._slideWidth = vw - 2 * this._peekPx;
      } else {
        this._peekPx = 0;
        this._slideWidth = (vw - (spv - 1) * this._gap) / spv;
      }

      this.track.querySelectorAll('.columns-banners__item').forEach(item => {
        item.style.width = `${this._slideWidth}px`;
      });
    }

    getTranslateX(index) {
      return -(this.cloneCount + index) * (this._slideWidth + this._gap) + this.peekPx;
    }

    setPosition(animate) {
      const x = this.getTranslateX(this.currentIndex);
      if (animate) {
        // Set transition first, then flush layout so the browser registers the
        // transition-property change before the transform change. Without this
        // reflow, some browsers won't animate when coming from transition:none.
        this.track.style.transition = 'transform 0.45s ease';
        this.track.getBoundingClientRect();
        this.track.style.transform = `translateX(${x}px)`;
      } else {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${x}px)`;
      }
    }

    _silentJump(index) {
      this.currentIndex = index;
      this.track.style.transition = 'none';
      this.track.getBoundingClientRect(); // flush so transition:none is applied
      this.track.style.transform = `translateX(${this.getTranslateX(index)}px)`;
    }

    next() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentIndex++;
      this.setPosition(true);
      this.track.addEventListener('transitionend', () => {
        this.isAnimating = false;
        if (this.currentIndex >= this.totalSlides) this._silentJump(0);
      }, { once: true });
    }

    prev() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentIndex--;
      this.setPosition(true);
      this.track.addEventListener('transitionend', () => {
        this.isAnimating = false;
        if (this.currentIndex < 0) this._silentJump(this.totalSlides - 1);
      }, { once: true });
    }

    bindEvents() {
      this.prevBtn.addEventListener('click', () => this.prev());
      this.nextBtn.addEventListener('click', () => this.next());

      this.track.addEventListener('touchstart', e => {
        this.startY = e.touches[0].clientY;
        this._touchLocked = false;
        this.dragStart(e.touches[0].clientX);
      }, { passive: true });
      this.track.addEventListener('touchmove', e => this.dragMoveTouch(e), { passive: false });
      this.track.addEventListener('touchend', () => this.dragEnd());

      this._onMouseMove = e => this.dragMove(e.clientX);
      this._onMouseUp = () => this.dragEnd();

      this.track.addEventListener('mousedown', e => {
        e.preventDefault();
        this.dragStart(e.clientX);
        this.isDragging = true;
      });
      window.addEventListener('mousemove', this._onMouseMove);
      window.addEventListener('mouseup', this._onMouseUp);
    }

    dragStart(x) {
      if (this.isAnimating) return;
      this.isDragging = false;
      this.startX = x;
      this.dragDelta = 0;
      this.track.style.transition = 'none';
    }

    dragMoveTouch(e) {
      const touch = e.touches[0];
      const dx = touch.clientX - this.startX;
      const dy = touch.clientY - this.startY;

      if (!this.isDragging && !this._touchLocked) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (Math.abs(dy) > Math.abs(dx)) { this._touchLocked = true; this.isDragging = false; return; }
        this.isDragging = true;
      }

      if (!this.isDragging) return;
      e.preventDefault();
      this.dragMove(touch.clientX);
    }

    dragMove(x) {
      if (!this.isDragging) return;
      this.dragDelta = x - this.startX;
      const base = this.getTranslateX(this.currentIndex);
      this.track.style.transform = `translateX(${base + this.dragDelta}px)`;
    }

    dragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      const threshold = Math.max(50, this._slideWidth * 0.2);
      if (this.dragDelta < -threshold) {
        this.next();
      } else if (this.dragDelta > threshold) {
        this.prev();
      } else {
        this.setPosition(true);
      }
    }
  }

  customElements.define('columns-banners-slider', ColumnsBannersSlider);
}
